import { Link } from "@/i18n/navigation";
import type { ComponentProps } from "react";

const base =
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full font-semibold transition-[background-color,color,border-color,box-shadow,transform] duration-200 ease-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 active:translate-y-px disabled:opacity-60 disabled:pointer-events-none";

const sizes = {
  md: "px-5 py-2.5 text-sm",
  lg: "px-7 py-3.5 text-base",
};

// Button hierarchy:
//   primary   — deep forest, the one main action on a view
//   clay      — copper, the "book / reserve" accent (used sparingly)
//   secondary — quiet outlined button on a white surface
//   ghost     — text-only
const variants = {
  primary:
    "bg-forest-500 text-white hover:bg-forest-600 shadow-sm shadow-forest-700/20",
  secondary:
    "bg-cream text-bark border border-sand hover:border-forest-400 hover:text-forest-600",
  clay: "bg-clay-solid text-white hover:bg-clay-solid-hover shadow-sm shadow-clay-solid-hover/25",
  ghost: "text-bark hover:text-forest-600",
};

type Variant = keyof typeof variants;
type Size = keyof typeof sizes;

export function buttonClass(variant: Variant = "primary", size: Size = "md") {
  return `${base} ${sizes[size]} ${variants[variant]}`;
}

type LinkButtonProps = ComponentProps<typeof Link> & {
  variant?: Variant;
  size?: Size;
};

// A locale-aware link styled as a button.
export function LinkButton({
  variant = "primary",
  size = "md",
  className = "",
  ...props
}: LinkButtonProps) {
  return (
    <Link className={`${buttonClass(variant, size)} ${className}`} {...props} />
  );
}

type ButtonProps = ComponentProps<"button"> & {
  variant?: Variant;
  size?: Size;
};

export function Button({
  variant = "primary",
  size = "md",
  className = "",
  ...props
}: ButtonProps) {
  return (
    <button className={`${buttonClass(variant, size)} ${className}`} {...props} />
  );
}
